import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { patchOf } from "../common/patch.js";
import { timestampSchema, uuidSchema } from "../common/primitives.js";
import { emailSchema, newPasswordSchema } from "./auth.js";
import { permissionSchema, scopeTypeSchema } from "./permissions.js";
/**
 * Rol ataması girdisi: TENANT kapsamında scopeId verilmez, diğerlerinde zorunludur. `COUNTERPARTY` (Prompt 17, ADR 0017
 * §17) YALNIZCA `FLEET_PORTAL_USER` rolüyle ve etkin, portalı açık filo hesabı olan cariye atanır (sunucuda denetlenir).
 */
export const roleAssignmentInputSchema = z
    .object({ roleId: uuidSchema, scopeType: scopeTypeSchema, scopeId: uuidSchema.nullable().default(null) })
    .refine((value) => (value.scopeType === "TENANT") === (value.scopeId === null), {
    path: ["scopeId"],
    message: "Kiracı kapsamında scopeId boş olmalı; şirket/bölge/istasyon/cari kapsamında zorunludur",
});
// ── Roller ───────────────────────────────────────────────────────────────────
export const roleSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    isSystem: z.boolean().describe("Sistem rolleri düzenlenemez"),
    systemKey: z.string().nullable(),
    isActive: z.boolean(),
    permissions: z.array(permissionSchema),
});
export const roleListSchema = z.object({ items: z.array(roleSchema) });
export const createRoleSchema = z.object({
    name: z.string().trim().min(1, "Rol adı zorunludur").max(100),
    description: z.string().trim().max(500).nullable().default(null),
    permissions: z.array(permissionSchema).min(1, "En az bir izin seçin"),
});
export const updateRoleSchema = patchOf(createRoleSchema).extend({ isActive: z.boolean().optional() });
export const permissionCatalogSchema = z.object({
    items: z.array(z.object({ code: permissionSchema, module: z.string(), description: z.string(), stepUp: z.boolean() })),
});
// ── Kullanıcılar ─────────────────────────────────────────────────────────────
export const tenantUserSchema = z.object({
    userId: uuidSchema,
    email: z.string(),
    fullName: z.string(),
    membershipStatus: z.enum(["ACTIVE", "SUSPENDED", "REMOVED"]),
    mfaEnabled: z.boolean(),
    lastLoginAt: timestampSchema.nullable(),
    assignments: z.array(z.object({
        id: uuidSchema,
        roleId: uuidSchema,
        roleName: z.string(),
        scopeType: scopeTypeSchema,
        scopeId: uuidSchema.nullable(),
        scopeName: z.string().nullable(),
    })),
});
export const tenantUserListSchema = z.object({ items: z.array(tenantUserSchema) });
export const userIdParamsSchema = z.object({ userId: uuidSchema });
export const setUserRolesSchema = z.object({ assignments: z.array(roleAssignmentInputSchema).max(50) });
export const setMembershipStatusSchema = z.object({ status: z.enum(["ACTIVE", "SUSPENDED", "REMOVED"]) });
// ── Davetler ─────────────────────────────────────────────────────────────────
export const invitationStatusSchema = z.enum(["PENDING", "ACCEPTED", "REVOKED", "EXPIRED"]);
export const invitationSchema = z.object({
    id: uuidSchema,
    email: z.string(),
    fullName: z.string().nullable(),
    status: invitationStatusSchema,
    assignments: z.array(z.object({ roleId: uuidSchema, scopeType: scopeTypeSchema, scopeId: uuidSchema.nullable() })),
    expiresAt: timestampSchema,
    createdAt: timestampSchema,
});
export const invitationPageSchema = pageOf(invitationSchema);
export const listInvitationsQuerySchema = pageQuerySchema.extend({
    status: invitationStatusSchema.optional(),
});
export const createInvitationSchema = z.object({
    email: emailSchema,
    fullName: z.string().trim().max(200).optional(),
    assignments: z.array(roleAssignmentInputSchema).min(1, "En az bir rol seçin").max(20),
});
export const invitationIdParamsSchema = z.object({ id: uuidSchema });
export const invitationLookupQuerySchema = z.object({ token: z.string().min(20).max(300) });
export const invitationLookupSchema = z.object({
    email: z.string(),
    tenantName: z.string(),
    /** true → bu e-postayla hesap var; kabul için parola belirlenmez, giriş yapılmış olmalıdır. */
    accountExists: z.boolean(),
    roleNames: z.array(z.string()),
});
export const acceptInvitationSchema = z.object({
    token: z.string().min(20).max(300),
    /** Yeni hesap oluşturulacaksa zorunlu; mevcut hesapta gönderilmez. */
    fullName: z.string().trim().min(1).max(200).optional(),
    password: newPasswordSchema.optional(),
});
export const acceptInvitationResponseSchema = z.object({
    tenantId: uuidSchema,
    userId: uuidSchema,
    accountCreated: z.boolean(),
});
export const tenantSecuritySchema = z.object({ mfaRequired: z.boolean() });
//# sourceMappingURL=iam.js.map